import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_CHAT_MODEL, type ChatModelId } from '@/lib/ai/chat-models';
import type { RerankerId } from '@/lib/ai/rerankers';

/**
 * Izbira chat LLM modela (klient). Persistira se v localStorage, pošlje se na
 * /api/chat ob vsaki poizvedbi. Preklop nazaj na Gemini = izbira 'gemini-flash'.
 *
 * Custom polja so relevantna le za 'custom' (admin) — lasten OpenAI-compatible
 * endpoint (CLORE.AI ipd.). Server neodvisno preveri admin pravico.
 */
interface ModelStore {
  modelId: ChatModelId;
  customBaseUrl: string;
  customModel: string;
  customApiKey: string;
  /** Opt-in rerank (GraphRAG korak 3c; CE ali Flash listwise); 'none' = brez (privzeto). */
  rerankerId: RerankerId;
  setModelId: (id: ChatModelId) => void;
  setRerankerId: (id: RerankerId) => void;
  setCustom: (patch: Partial<{ customBaseUrl: string; customModel: string; customApiKey: string }>) => void;
  /** Payload za /api/chat body (samo custom polja, ko je izbran custom). */
  getModelPayload: () => {
    id: ChatModelId;
    customBaseUrl?: string;
    customModel?: string;
    customApiKey?: string;
  };
  /** Payload `rerank` za /api/chat body (undefined pri 'none' — brez šuma v telesu). */
  getRerankPayload: () => { id: string } | undefined;
}

export const useModelStore = create<ModelStore>()(
  persist(
    (set, get) => ({
      modelId: DEFAULT_CHAT_MODEL,
      customBaseUrl: '',
      customModel: '',
      customApiKey: '',
      rerankerId: 'none',
      setModelId: (modelId) => set({ modelId }),
      setRerankerId: (rerankerId) => set({ rerankerId }),
      setCustom: (patch) => set(patch),
      getModelPayload: () => {
        const s = get();
        if (s.modelId === 'custom') {
          return {
            id: s.modelId,
            customBaseUrl: s.customBaseUrl || undefined,
            customModel: s.customModel || undefined,
            customApiKey: s.customApiKey || undefined,
          };
        }
        return { id: s.modelId };
      },
      getRerankPayload: () => {
        const s = get();
        return s.rerankerId === 'none' ? undefined : { id: s.rerankerId };
      },
    }),
    {
      name: 'tribonianus-model',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
